import { constructPlayerThroughArmorClassFromBase } from './from-base-player-ac';
import { createFromBasePlayerGrantContext } from './from-base-player-grants';
import type { FromBasePlayerFeatureMetadata } from './from-base-player-features';
import { createFromBasePlayerSourceTypeReader } from './from-base-player-integers';
import { createComputedPersistence } from './computed-persistence';
import { compileCreatureCarryingCapacity, carryingCapacityFactor } from './creature-carrying-capacity';
import { compileCreatureMeterCaps } from './creature-meter-caps';
import { creatureSizeUuidFromCell } from './creature-size';
import { readItemNumericSlot } from './enchantment-template';
import { readItemField } from './item-field-reader';
import { phpJsonInt } from './php-json-values';
import { phpJsonFloat } from './php-decimal-values';
import { surfaceField } from './from-base-encounter-media';
import { fromBaseClone, fromBaseRecord, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBasePlayerInput, FromBaseRow as Row } from './from-base-context';

/** Native post-AC capacity and PC meter window. Physics height, bender and
 * consequence passes remain separate gaps; this is never a runtime member. */
export function constructEncounterMemberCapacityFromBase(base: FromBasePlayerInput,
    metadata: FromBasePlayerFeatureMetadata, input: Row): Row {
    const { readers, common } = createFromBasePlayerGrantContext(base, metadata, input, 'member-capacity-meters');
    const sizes = metadata.inventoryMetadata?.sizeCatalog;
    if (!sizes?.length) throw new FromBaseInputMissing('member-size-catalog', base.entity_id);
    const sizeId = creatureSizeUuidFromCell(readers.layerValue(input, 'size'));
    const size = sizes.find(row => readItemField(row, 'item_id') === sizeId);
    const robust = readers.layerValue(input, 'robust_build') !== null
        || Object.keys(readers.layerEntries(input, 'robust_build')).length > 0;
    const rank = sizes.findIndex(row => readItemField(row, 'item_id') === sizeId);
    const capacitySize = robust ? sizes[Math.min((rank < 0 ? 0 : rank) + 1, sizes.length - 1)] : size;
    const multiplier = phpJsonFloat(readItemField(capacitySize ?? null, 'weight_multiplier') ?? 1);
    // This partial fallback never reaches the final fold. Keep its refusal;
    // full memberCompiler calls use the PC/NPC resource builders and final fold.
    if (carryingCapacityFactor(readers.layerValue(input, 'carrying_capacity_multiplier')) !== 1)
        throw new FromBaseStageMissing('member-capacity-browser-fold', base.entity_id);
    const stored = (row: unknown, name: string) => surfaceField(fromBaseRecord(row) ? row : null, name);
    const inventory = stored(stored(stored(input, 'computed'), 'creature'), 'inventory');
    const held = stored(stored(inventory, 'total_weight_held'), 'total')
        ?? surfaceField(input, 'total_weight_held') ?? 0;
    const capacity = compileCreatureCarryingCapacity({
        body: phpJsonInt(readItemNumericSlot(readers.layerValue(input, 'body') ?? 0)),
        weightMultiplier: multiplier, weightHeld: phpJsonInt(held),
    });
    const persist = createComputedPersistence({ ...common,
        queryItemType: createFromBasePlayerSourceTypeReader(base, metadata, input),
        resolveItemName: id => readers.itemField(common.resolveCarrierRef(id), 'item_name') ?? '' });
    let host = fromBaseClone(input);
    host = persist(host, 'size', 'base', phpJsonInt(readItemField(capacitySize ?? null, 'base_size'))) as Row;
    host = persist(host, 'size', 'weight_multiplier', phpJsonInt(multiplier)) as Row;
    ((host.computed as Row).creature as Row).capacity = capacity;
    const caps = compileCreatureMeterCaps({ itemType: String(readers.itemField(input, 'item_type') ?? ''), sizeId,
        sizeData: size ? { hungerMeterMax: phpJsonInt(readItemField(size, 'hunger_meter_max')),
            thirstMeterMax: phpJsonInt(readItemField(size, 'thirst_meter_max')) } : null });
    if (readers.itemField(input, 'item_type') === 'player_character' && sizeId && size) {
        for (const key of ['hunger_max', 'thirst_max'] as const) host = persist(host, 'meters', key, caps[key] ?? 0) as Row;
    }
    return host;
}

export function constructEncounterMemberThroughCapacityFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerFeatureMetadata) {
    const prior = constructPlayerThroughArmorClassFromBase(base, metadata);
    if (prior.kind !== 'constructed-prefix') return prior;
    try {
        return { ...prior, row: constructEncounterMemberCapacityFromBase(base, metadata, prior.row),
            completedStages: [...prior.completedStages, 'member-capacity-meters'],
            missingStages: [...prior.missingStages, 'member-physics-height', 'member-spellcasting-essence'] };
    } catch (error) {
        if (error instanceof FromBaseInputMissing) return { kind: 'missing-input' as const,
            stage: 'member-capacity-meters', dependency: error.dependency, id: error.id };
        if (error instanceof FromBaseStageMissing) return { kind: 'missing-stage' as const, stage: error.stage, detail: error.detail };
        throw error;
    }
}
